import { format } from 'date-fns';
import i18n from '@/i18n';
import { isCompleted } from '@/constants/matchStatus';
import type { StandingsGroup } from '@/types/league';
import type { BracketMatch, BracketRound, BracketSide, BracketTie } from '@/utils/knockoutBracket';
import { getStageLabel } from '@/utils/matchdayHelpers';

/**
 * What the play-off bracket shows for the model of `utils/knockoutBracket`:
 * texts of rounds, sides and card captions, the round it opens at and how the
 * round columns line up. Pure; the model is read, never changed.
 */

type Rounds = readonly BracketRound<BracketMatch>[];

/** Place in the table by team name; empty unless the league has exactly one table. */
export function tableRanks(groups: readonly StandingsGroup[] | undefined): Map<string, number> {
  const rows = groups?.length === 1 ? groups[0].rows : [];
  return new Map(rows.map(row => [row.team.name, row.rank]));
}

/** Name of a round; the round found by position has no stored name of its own. */
export function roundLabel(round: BracketRound<BracketMatch>): string {
  return round.stage ? getStageLabel(round.stage) : i18n.t('league:bracketKnockoutPlayoffs');
}

const ordinal = (place: number): string => i18n.t('league:bracketOrdinal', { count: place, ordinal: true });

/** "15-18" for a run of neighbouring places, "9/10/23/24" otherwise. */
function placeList(ranks: number[]): string {
  const sorted = [...ranks].sort((a, b) => a - b);
  const isRun = sorted.length > 2 && sorted.every((place, i) => place === sorted[0] + i);
  return isRun ? `${sorted[0]}-${sorted[sorted.length - 1]}` : sorted.join('/');
}

/** Team name of a side, or the text that says who takes it. */
export function sideLabel(side: BracketSide, rounds: Rounds): string {
  const slot = side.placeholder;
  if (!slot) return side.team ?? '';
  if (slot.kind === 'ranks') {
    const places = slot.ranks.map(ordinal).join(' / ');
    return i18n.t(slot.ranks.length === 1 ? 'league:bracketPlace' : 'league:bracketPlaces', { places });
  }
  const source = rounds[slot.round];
  if (slot.kind === 'winnerOfRanks') {
    const ranks = placeList(slot.ranks);
    return source.stage
      ? i18n.t('league:bracketRoundWinnerOfPlaces', { round: roundLabel(source), ranks })
      : i18n.t('league:bracketWinnerOfPlaces', { ranks });
  }
  if (slot.kind === 'winnerOfSlot') {
    return i18n.t('league:bracketRoundWinnerOfSlot', { round: roundLabel(source), slot: slot.slot });
  }
  return i18n.t('league:bracketRoundWinner', { round: roundLabel(source) });
}

const kickoffDay = (iso: string): string =>
  new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }).format(new Date(iso));

/**
 * Header of a tie card: what its numbers are. `null` when nothing is known -
 * a round a cup has not stored yet does not say how many matches a tie has.
 */
export function tieCaption(tie: BracketTie<BracketMatch>, round: BracketRound<BracketMatch>): string | null {
  if (tie.legs.length === 0) {
    if (tie.sides[0].placeholder?.kind === 'winnerOfRound') return null;
    return i18n.t(round.legs === 2 ? 'league:bracketTwoLegs' : 'league:bracketOneLeg');
  }
  const played = tie.legs.filter(leg => leg.score_team1 != null && leg.score_team2 != null).length;
  if (round.legs === 1) {
    const day = kickoffDay(tie.legs[0].match_date);
    return played > 0 ? day : `${day}, ${format(new Date(tie.legs[0].match_date), 'HH:mm')}`;
  }
  if (played === 0) return tie.legs.map(leg => kickoffDay(leg.match_date)).join(' / ');
  return played < round.legs
    ? i18n.t('league:bracketAfterLeg', { number: played })
    : i18n.t('league:bracketAggregate');
}

/** The round the bracket opens at: the first one not played out, else the last. */
export function openRoundIndex(rounds: Rounds): number {
  const open = rounds.findIndex(round =>
    round.ties.some(tie => tie.legs.length < round.legs || !tie.legs.every(leg => isCompleted(leg.status))),
  );
  return open === -1 ? rounds.length - 1 : open;
}

export interface ColumnLayout {
  /** The pairing with the previous round is not known: the column starts a new group. */
  startsGroup: boolean;
  /** Height of one tie in slots of the first round of its group that is on screen. */
  span: number;
  /** A line leaves every tie towards the next round. */
  linksNext: boolean;
  /** Two neighbouring ties join into one tie of the next round. */
  merges: boolean;
}

/**
 * How the round columns line up: groups of rounds joined by connector lines.
 *
 * @param firstVisible index of the first round on screen; the rounds before it take no part
 */
export function columnLayout(rounds: Rounds, firstVisible: number): ColumnLayout[] {
  let baseTies = 0;
  return rounds.map((round, r) => {
    const startsGroup = r === 0 || !rounds[r - 1].connectsToNext;
    if (startsGroup || r <= firstVisible) baseTies = round.ties.length;
    return {
      startsGroup,
      span: baseTies / round.ties.length,
      linksNext: round.connectsToNext,
      merges: round.connectsToNext && round.ties.length === rounds[r + 1].ties.length * 2,
    };
  });
}
